import { useEffect, useState } from "react";

const STORAGE_KEY = "aurelia-recently-viewed";
const MAX_ENTRIES = 12;

export interface RecentlyViewedEntry {
  slug: string;
  name: string;
  imageUrl: string | null;
  basePrice: number;
  compareAtPrice: number | null;
}

function readAll(): RecentlyViewedEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Records the current product as viewed — no user/account data, just the
// handful of fields needed to render a card, capped so storage can't grow
// unbounded.
export function recordRecentlyViewed(entry: RecentlyViewedEntry) {
  const existing = readAll().filter((e) => e.slug !== entry.slug);
  const next = [entry, ...existing].slice(0, MAX_ENTRIES);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full or unavailable (private browsing) — not worth surfacing.
  }
}

// Returns everything viewed except the given slug (so a product page never
// lists itself under "Recently viewed").
export function useRecentlyViewed(excludeSlug?: string): RecentlyViewedEntry[] {
  const [entries, setEntries] = useState<RecentlyViewedEntry[]>([]);

  useEffect(() => {
    setEntries(readAll());
  }, [excludeSlug]);

  return entries.filter((e) => e.slug !== excludeSlug);
}
